import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import * as apiService from 'api/apiServices';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { useToast } from 'context/ToastContext';
import { useService } from './context/ServiceProvider';
import useDeleteService from './hooks/hookDeleteService';
import ServiceFilterStatus from './ServiceToolBar/ServiceFilterStatus';
import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import EToolTip from 'components/others/EToolTip';
import { useNeedPay } from 'modules/admin';

interface ServiceToolBarProps {
    fetchServices: (force?: boolean) => Promise<unknown>;
    filterStatus: number[];
    setFilterStatus: Dispatch<SetStateAction<number[]>>;
}

const ServiceToolBar = ({ fetchServices, filterStatus, setFilterStatus }: ServiceToolBarProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Services', 'common']);
    const { showToast } = useToast();
    const { setShowEditService, serviceCheck, setServiceCheck, setShowSpinner, triggerListReset, triggerListRefresh } =
        useService();
    const { archiveService } = useDeleteService();
    const { needPay } = useNeedPay();

    const cbArchiveService = async () => {
        setShowSpinner(true);
        const resActive = await apiService.setStat(serviceCheck, 0);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchServices(true);
        setShowSpinner(false);
        setServiceCheck([]);
    };

    const handleClickActive = async () => {
        setShowSpinner(true);
        const resActive = await apiService.setStat(serviceCheck, 1);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchServices(true);
        setShowSpinner(false);
        setServiceCheck([]);
    };

    return (
        <div className="mb-3" style={{ minWidth: '100px' }}>
            <div className="page-toolbar-actions">
                {hasUserAccess(auth?.user, 'prodEdit') && (
                    <>
                        <Button
                            className="fs-14 ms-1"
                            size="sm"
                            variant="success"
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (needPay()) return;
                                setShowEditService(0);
                            }}
                        >
                            <i className="bi bi-plus fs-14 p-0"></i>
                            <span className="d-none d-md-inline ms-1">{t('toolAddService')}</span>
                        </Button>

                        <EToolTip title={t('toolArchive')}>
                            <span className="d-inline-block ms-1">
                                <Button
                                    className="fs-14 px-2 toolbar-icon-btn"
                                    size="sm"
                                    variant="danger"
                                    disabled={!serviceCheck?.length}
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        archiveService(cbArchiveService);
                                    }}
                                >
                                    <i className="bi bi-archive fs-14 p-0"></i>
                                </Button>
                            </span>
                        </EToolTip>

                        <EToolTip title={t('toolActive')}>
                            <span className="d-inline-block ms-1">
                                <Button
                                    className="fs-14 px-2 toolbar-icon-btn"
                                    size="sm"
                                    variant="success"
                                    disabled={!serviceCheck?.length}
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        void handleClickActive();
                                    }}
                                >
                                    <i className="bi bi-check2 fs-14 p-0"></i>
                                </Button>
                            </span>
                        </EToolTip>
                    </>
                )}

                <ListToolBarActions onReset={triggerListReset} onRefresh={triggerListRefresh} />

                <ServiceFilterStatus filterStatus={filterStatus} setFilterStatus={setFilterStatus} />
            </div>
        </div>
    );
};

export default ServiceToolBar;
